import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useServerWebSocket } from '../lib/ws.js';
import { PlayerInfo } from '@kcdash/shared';
import { Modal } from '../components/Modal.js';
import {
  Users,
  UserCheck,
  UserX,
  MessageSquare,
  Compass,
  Clock,
  Wifi,
  ShieldAlert,
} from 'lucide-react';

export const PlayersPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [players, setPlayers] = useState<PlayerInfo[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Modals state
  const [kickTarget, setKickTarget] = useState<PlayerInfo | null>(null);
  const [kickReason, setKickReason] = useState('Violating server rules');

  const [banTarget, setBanTarget] = useState<PlayerInfo | null>(null);
  const [banReason, setBanReason] = useState('Cheating or severe disruption');
  const [banDuration, setBanDuration] = useState(60);

  const [messageTarget, setMessageTarget] = useState<PlayerInfo | null>(null);
  const [messageText, setMessageText] = useState('');

  const [teleportTarget, setTeleportTarget] = useState<PlayerInfo | null>(null);
  const [teleportCoords, setTeleportCoords] = useState({ x: 0, y: 0, z: 0 });

  const { isConnected } = useServerWebSocket({
    serverId: id,
    onPlayersChange: (updated) => {
      setPlayers(updated);
    },
  });

  const loadPlayers = async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const data = await api.getPlayers(id);
      setPlayers(data);
    } catch (err) {
      console.error('Failed to load players:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadPlayers();
  }, [id]);

  const handleKick = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !kickTarget) return;
    try {
      await api.kickPlayer(id, kickTarget.id, kickReason);
      setKickTarget(null);
      await loadPlayers();
    } catch (err: any) {
      alert(`Kick failed: ${err.message}`);
    }
  };

  const handleBan = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !banTarget) return;
    try {
      await api.banPlayer(id, banTarget.id, banReason, Number(banDuration));
      setBanTarget(null);
      await loadPlayers();
    } catch (err: any) {
      alert(`Ban failed: ${err.message}`);
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !messageTarget || !messageText.trim()) return;
    try {
      await api.sendMessage(id, messageTarget.id, messageText.trim());
      setMessageTarget(null);
      setMessageText('');
      alert('Message sent to player.');
    } catch (err: any) {
      alert(`Message failed: ${err.message}`);
    }
  };

  const handleTeleport = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !teleportTarget) return;
    try {
      await api.teleportPlayer(id, teleportTarget.id, teleportCoords);
      setTeleportTarget(null);
      alert('Teleport command sent.');
    } catch (err: any) {
      alert(`Teleport failed: ${err.message}`);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-neutral-100">Connected Players</h2>
          <p className="text-xs text-neutral-400 mt-1 font-mono">
            Realtime player tracking, Steam identifiers, and remote moderation bridge
          </p>
        </div>

        <div className="flex items-center gap-2 font-mono text-xs text-neutral-400 bg-panel-card border border-panel-border px-3 py-1.5 rounded-lg">
          <Users className="w-4 h-4 text-brand-400" />
          <span>{players.length} Active Players</span>
        </div>
      </div>

      <div className="bg-panel-card border border-panel-border rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs font-mono">
            <thead className="bg-panel-subtle/80 border-b border-panel-border text-neutral-400 uppercase select-none">
              <tr>
                <th className="px-5 py-3">Player</th>
                <th className="px-5 py-3">Player ID</th>
                <th className="px-5 py-3">Steam ID</th>
                <th className="px-5 py-3">Role / Faction</th>
                <th className="px-5 py-3">Ping</th>
                <th className="px-5 py-3 text-right">Moderation</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-panel-border/50 text-neutral-300">
              {isLoading ? (
                <tr>
                  <td colSpan={6} className="py-12 text-center text-neutral-500">
                    Loading player registry...
                  </td>
                </tr>
              ) : players.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-12 text-center text-neutral-500">
                    No players are currently connected to this server.
                  </td>
                </tr>
              ) : (
                players.map((p) => (
                  <tr key={p.id} className="hover:bg-panel-cardHover transition">
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-brand-950 border border-brand-800 flex items-center justify-center text-brand-400 font-bold text-[10px]">
                          {p.nickname.substring(0, 2).toUpperCase()}
                        </div>
                        <div>
                          <div className="font-semibold text-neutral-100">{p.nickname}</div>
                          {p.characterName && p.characterName !== p.nickname && (
                            <div className="text-[10px] text-neutral-500">{p.characterName}</div>
                          )}
                        </div>
                      </div>
                    </td>

                    <td className="px-5 py-3.5 text-neutral-400">#{p.id}</td>

                    <td className="px-5 py-3.5 text-neutral-400">{p.steamId || 'N/A'}</td>

                    <td className="px-5 py-3.5 text-brand-400 font-medium">{p.role || 'Citizen'}</td>

                    <td className="px-5 py-3.5 text-neutral-400">
                      <span className="flex items-center gap-1">
                        <Wifi className="w-3 h-3 text-emerald-400" />
                        {p.ping} ms
                      </span>
                    </td>

                    <td className="px-5 py-3.5 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          onClick={() => setMessageTarget(p)}
                          title="Send in-game message"
                          className="p-1.5 rounded-lg bg-panel-subtle hover:bg-neutral-800 text-neutral-400 hover:text-neutral-200 transition"
                        >
                          <MessageSquare className="w-3.5 h-3.5" />
                        </button>

                        <button
                          onClick={() => setTeleportTarget(p)}
                          title="Teleport player"
                          className="p-1.5 rounded-lg bg-panel-subtle hover:bg-neutral-800 text-neutral-400 hover:text-cyan-400 transition"
                        >
                          <Compass className="w-3.5 h-3.5" />
                        </button>

                        <button
                          onClick={() => setKickTarget(p)}
                          title="Kick player"
                          className="p-1.5 rounded-lg bg-panel-subtle hover:bg-amber-950/60 text-neutral-400 hover:text-amber-400 transition"
                        >
                          <UserX className="w-3.5 h-3.5" />
                        </button>

                        <button
                          onClick={() => setBanTarget(p)}
                          title="Ban player"
                          className="p-1.5 rounded-lg bg-panel-subtle hover:bg-red-950/60 text-neutral-400 hover:text-red-400 transition"
                        >
                          <ShieldAlert className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Kick Modal */}
      {kickTarget && (
        <Modal isOpen={true} onClose={() => setKickTarget(null)} title={`Kick ${kickTarget.nickname}`} maxWidth="sm">
          <form onSubmit={handleKick} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-neutral-300 uppercase mb-1">Reason</label>
              <input
                type="text"
                required
                value={kickReason}
                onChange={(e) => setKickReason(e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-panel-input border border-panel-border text-sm text-neutral-200 focus:outline-none focus:border-brand-500"
              />
            </div>
            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setKickTarget(null)}
                className="px-3 py-1.5 text-xs text-neutral-400 hover:text-neutral-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-1.5 rounded-lg text-xs font-medium bg-amber-600 hover:bg-amber-500 text-black font-semibold"
              >
                Confirm Kick
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Ban Modal */}
      {banTarget && (
        <Modal isOpen={true} onClose={() => setBanTarget(null)} title={`Ban ${banTarget.nickname}`} maxWidth="sm">
          <form onSubmit={handleBan} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-neutral-300 uppercase mb-1">Reason</label>
              <input
                type="text"
                required
                value={banReason}
                onChange={(e) => setBanReason(e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-panel-input border border-panel-border text-sm text-neutral-200 focus:outline-none focus:border-brand-500"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-neutral-300 uppercase mb-1">
                Duration (Minutes, 0 = Permanent)
              </label>
              <input
                type="number"
                min={0}
                value={banDuration}
                onChange={(e) => setBanDuration(Number(e.target.value))}
                className="w-full px-3 py-2 rounded-lg bg-panel-input border border-panel-border text-sm text-neutral-200 focus:outline-none focus:border-brand-500 font-mono"
              />
            </div>

            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setBanTarget(null)}
                className="px-3 py-1.5 text-xs text-neutral-400 hover:text-neutral-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-1.5 rounded-lg text-xs font-medium bg-red-600 hover:bg-red-500 text-white font-semibold"
              >
                Confirm Ban
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Message Modal */}
      {messageTarget && (
        <Modal isOpen={true} onClose={() => setMessageTarget(null)} title={`Message to ${messageTarget.nickname}`} maxWidth="sm">
          <form onSubmit={handleSendMessage} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-neutral-300 uppercase mb-1">Private Notification</label>
              <textarea
                rows={3}
                required
                placeholder="Notice: Admin maintenance in 10 minutes..."
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-panel-input border border-panel-border text-sm text-neutral-200 focus:outline-none focus:border-brand-500"
              />
            </div>
            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setMessageTarget(null)}
                className="px-3 py-1.5 text-xs text-neutral-400 hover:text-neutral-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-1.5 rounded-lg text-xs font-medium bg-brand-600 hover:bg-brand-500 text-white"
              >
                Send Message
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Teleport Modal */}
      {teleportTarget && (
        <Modal isOpen={true} onClose={() => setTeleportTarget(null)} title={`Teleport ${teleportTarget.nickname}`} maxWidth="sm">
          <form onSubmit={handleTeleport} className="space-y-4">
            <div className="grid grid-cols-3 gap-2 font-mono">
              <div>
                <label className="block text-xs font-semibold text-neutral-300 mb-1">X</label>
                <input
                  type="number"
                  value={teleportCoords.x}
                  onChange={(e) => setTeleportCoords({ ...teleportCoords, x: Number(e.target.value) })}
                  className="w-full px-2 py-1.5 rounded bg-panel-input border border-panel-border text-xs text-neutral-200"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-neutral-300 mb-1">Y</label>
                <input
                  type="number"
                  value={teleportCoords.y}
                  onChange={(e) => setTeleportCoords({ ...teleportCoords, y: Number(e.target.value) })}
                  className="w-full px-2 py-1.5 rounded bg-panel-input border border-panel-border text-xs text-neutral-200"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-neutral-300 mb-1">Z</label>
                <input
                  type="number"
                  value={teleportCoords.z}
                  onChange={(e) => setTeleportCoords({ ...teleportCoords, z: Number(e.target.value) })}
                  className="w-full px-2 py-1.5 rounded bg-panel-input border border-panel-border text-xs text-neutral-200"
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setTeleportTarget(null)}
                className="px-3 py-1.5 text-xs text-neutral-400 hover:text-neutral-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-1.5 rounded-lg text-xs font-medium bg-brand-600 hover:bg-brand-500 text-white"
              >
                Execute Teleport
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
};
